class ProductInventory extends HTMLElement {
  connectedCallback() {
    this.noticeEl = this.querySelector('.js-inventory-notice');
    this.urgencyEl = this.querySelector('.js-inventory-urgency');
    this.indicatorEl = this.querySelector('.js-inventory-indicator');
    this.indicatorBarEl = this.indicatorEl ? this.indicatorEl.querySelector('span') : null;

    this.thresholdLow = this._toInt(this.dataset.thresholdLow, 2);
    this.thresholdVeryLow = this._toInt(this.dataset.thresholdVeryLow, 1);
    this.scale = this._toInt(this.dataset.scale, 35);

    this.textVeryLow = this.dataset.textVeryLow || '';
    this.textLow = this.dataset.textLow || '';
    this.textNormal = this.dataset.textNormal || '';
    this.textNoStock = this.dataset.textNoStock || '';
    this.textNoStockBackordered = this.dataset.textNoStockBackordered || '';
    this.textOtherVariant = this.dataset.textOtherVariant || 'In anderer Größe/Farbe verfügbar';

    this.showNoStockBackordered = (this.dataset.showNoStockBackordered || 'true') === 'true';

    // Variant JSON aus <script type="application/json"> innerhalb von <product-inventory>
    this.variantData = this._readVariantJson();

    // WICHTIG: anyInStock aus JSON berechnen (nicht aus dataset)
    this.anyInStock = this._computeAnyInStock();

    // Initial render mit den Liquid-Initialwerten (current_variant)
    this._renderFromDataset();

    // Update bei Variant-Wechsel
    this._bindVariantChangeListeners();
  }

  _computeAnyInStock() {
    if (!Array.isArray(this.variantData) || !this.variantData.length) return false;

    // "In stock" im Shop-Sinne:
    // - inventory_quantity > 0
    // - oder verfügbar trotz qty <=0 (z.B. policy continue), je nachdem wie du es bewerten willst.
    // Für deine Anforderung („nicht überall 'wir bestellen'“) reicht qty > 0 als Signal.
    return this.variantData.some(v => {
      const qty = this._toInt(v.inventory_quantity, 0);
      const available = (v.available === true || v.available === 'true');
      return available && qty > 0;
    });
  }

  _bindVariantChangeListeners() {
    const form =
      this.closest('.product')?.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    if (form) {
      form.addEventListener('change', (e) => {
        const idInput = form.querySelector('[name="id"]');
        if (!idInput) return;

        if (
          e.target &&
          (e.target.name === 'id' ||
            e.target.closest('[data-variant-picker]') ||
            e.target.closest('variant-picker') ||
            e.target.closest('custom-select'))
        ) {
          this.updateForVariantId(parseInt(idInput.value, 10));
        }
      });

      form.addEventListener('variant:change', (e) => {
        const id = e?.detail?.variant?.id;
        if (id) this.updateForVariantId(parseInt(id, 10));
      });

      document.addEventListener('variant:change', (e) => {
        const id = e?.detail?.variant?.id;
        if (id) this.updateForVariantId(parseInt(id, 10));
      });
    }

    window.addEventListener('popstate', () => {
      const params = new URLSearchParams(window.location.search);
      const id = params.get('variant');
      if (id) this.updateForVariantId(parseInt(id, 10));
    });
  }

  updateForVariantId(variantId) {
    if (!variantId || !this.variantData || !this.variantData.length) return;

    const v = this.variantData.find(x => parseInt(x.id, 10) === parseInt(variantId, 10));
    if (!v) return;

    // Dataset aktualisieren
    this.dataset.inventoryQuantity = this._toInt(v.inventory_quantity, 0);
    this.dataset.inventoryPolicy = (v.inventory_policy || '').toString();
    this.dataset.variantAvailable = (v.available === true || v.available === 'true') ? 'true' : 'false';

    // anyInStock ist global – kann aber bei dynamischen Fällen neu berechnet werden
    this.anyInStock = this._computeAnyInStock();

    this._renderFromDataset();
  }

  _renderFromDataset() {
    const qty = this._toInt(this.dataset.inventoryQuantity, 0);
    const policy = (this.dataset.inventoryPolicy || '').toString();
    const available = (this.dataset.variantAvailable || 'false') === 'true';

    let noticeText = '';
    let urgencyText = '';
    let level = 'normal';

    // 1) Out of stock für diese Variante
    if (qty <= 0 || !available) {
      // Wenn irgendeine andere Variante in Stock ist: NICHT „wir bestellen…“, sondern „in anderer Größe verfügbar“
      if (this.anyInStock) {
        noticeText = this.textOtherVariant;
        level = 'other-variant';
      } else {
        // Keine Variante verfügbar -> Backorder / No Stock
        if (policy === 'continue' && this.showNoStockBackordered && this.textNoStockBackordered) {
          noticeText = this._stripHtml(this.textNoStockBackordered);
          level = 'backordered';
        } else {
          noticeText = this._stripHtml(this.textNoStock) || 'Nicht auf Lager';
          level = 'no-stock';
        }
      }
    } else {
      // 2) In stock (qty > 0)
      if (qty <= this.thresholdVeryLow) {
        noticeText = this._stripHtml(this.textVeryLow) || 'Fast vergriffen';
        // Urgency NICHT doppeln – nur Zusatz setzen, wenn du wirklich willst:
        // urgencyText = 'Nur noch wenige verfügbar';
        level = 'very-low';
      } else if (qty <= this.thresholdLow) {
        noticeText = this._stripHtml(this.textLow) || 'Fast vergriffen';
        // urgencyText = 'Beeil dich – nur noch wenige da';
        level = 'low';
      } else {
        noticeText = this._stripHtml(this.textNormal) || 'Artikel verfügbar';
        level = 'normal';
      }
    }

    if (this.noticeEl) {
      this.noticeEl.textContent = noticeText || '';
    }

    if (this.urgencyEl) {
      if (urgencyText && (level === 'low' || level === 'very-low')) {
        this.urgencyEl.textContent = urgencyText;
        this.urgencyEl.style.display = '';
      } else {
        this.urgencyEl.textContent = '';
        this.urgencyEl.style.display = 'none';
      }
    }

    // Indicator-Bar
    if (this.indicatorEl && this.indicatorBarEl) {
      if (qty <= 0 || !available) {
        this.indicatorEl.hidden = true;
        this.indicatorBarEl.style.width = '0%';
      } else {
        this.indicatorEl.hidden = false;
        const pct = Math.max(0, Math.min(100, Math.round((qty / this.scale) * 100)));
        this.indicatorBarEl.style.width = `${pct}%`;
      }
    }

    // CSS Hook
    this.setAttribute('data-inventory-level', level);
  }

  _readVariantJson() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (e) {
      return [];
    }
  }

  _stripHtml(input) {
    if (!input) return '';
    const div = document.createElement('div');
    div.innerHTML = input;
    return (div.textContent || div.innerText || '').trim();
  }

  _toInt(val, fallback = 0) {
    const n = parseInt(val, 10);
    return Number.isFinite(n) ? n : fallback;
  }
}

customElements.define('product-inventory', ProductInventory);
